import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { RecipientInfoDto } from 'app/models/api-dto.model';
import { ReportListItemDto } from 'app/report-overview/report-list';
import { AnalyticsTrackEvent, TrackEventsHelper, TrackEventsService } from 'shared/analytics';
import { Options, Report, ReportForm, ReportType } from 'shared/report';
import { ReportPeriodService } from 'shared/report-period';
import { ReportTemplateAdapterService } from 'shared/report-template/report-template-adapter';

import { ReportEditionState, ShareType } from '../report/report.service.model';
import { ReportsConfiguration } from './configuration';
import {
    CreateReportDto,
    GetReportDto,
    GetReportEditionDto,
    GetReportEditionsDto,
    reportDtoHelper,
    UpdateReportDto,
} from './dto';
import { DuplicateReportDto, RegenerateEditionDto } from './dto/duplicate-report-dto.model';
import { ReportDetails } from './report-details';
import { ReportEditionDetails } from './report-edition-details';
import { ReportEditionListItem } from './report-edition-list-item';

/**
 * This is the date format to create a one-off or recurring report
 */
const REPORT_DATE_FORMAT = 'YYYY-MM-DD';

/**
 * Report Model service is responsible for the report data model
 */
@Injectable()
export class ReportModelService {
    private reportObject: Report;

    constructor(
        private readonly http: HttpClient,
        private readonly reportsConfiguration: ReportsConfiguration,
        private readonly reportPeriodService: ReportPeriodService,
        private readonly reportTemplateAdapter: ReportTemplateAdapterService,
        private readonly trackEventsService: TrackEventsService,
    ) {
        this.init();
    }

    /**
     * Initialize an empty report model
     */
    init() {
        this.reportObject = {
            guid: null,
            template: null,
            name: '',
            description: '',
            recurrency: ReportType.OneOff,
            periodStartDate: null,
            periodEndDate: null,
            periodSelected: undefined,
            startDate: null,
            publicationFrequency: '',
            users: [],
            shareList: [],
            siteAssets: [],
        };
    }

    set report(report: Report) {
        this.reportObject = report;
    }

    get report() {
        return this.reportObject;
    }

    set template(value: string) {
        this.report.template = value;
    }

    /**
     * Mutate the report model based on the changes from the report form.
     * @param {ReportForm} formGroups : this is a set of form groups within\
     *  the report form which include changes.
     */
    setFormGroups(formGroups: ReportForm) {
        const formGroupNames = Object.keys(formGroups);
        formGroupNames.forEach((formGroupName) => {
            this.mutateReport(formGroupName, formGroups);
        });
    }

    /**
     * We need to distinguish the difference between an empty form field
     * due to initialization of a form field or due to an empty value given by user input.
     * As a convention we use *null* as the initialization of the field.
     * In case of initialization we prefill the field from our report model since there are
     * scenario's the model already has the field value before initialization.
     *
     * @param {string} formField : value of form field
     * @param {string} modelField : value of model field
     */
    private setModelProp(formField: string, modelField: string) {
        return formField ?? modelField;
    }

    /**
     * Based on the given form group mutate the report model.
     * @param {string} formGroupName : mutated form group
     * @param {ReportForm} formGroups : list form groups
     */
    private mutateReport(formGroupName: string, formGroups: ReportForm) {
        switch (formGroupName) {
            case 'definition':
                this.report = {
                    ...this.report,
                    name: this.setModelProp(formGroups.definition.name, this.report.name),
                    description: this.setModelProp(
                        formGroups.definition.description,
                        this.report.description,
                    ),
                };
                break;
            case 'period':
                this.report = {
                    ...this.report,
                    periodStartDate: formGroups?.period?.dates?.startDate,
                    periodEndDate: formGroups?.period?.dates?.endDate,
                    periodSelected: formGroups?.period?.selected,
                };
                break;
            case 'recurrency':
                this.report = {
                    ...this.report,
                    recurrency: formGroups?.recurrency?.recurrency,
                };
                break;
            case 'publicationFrequency':
                this.report = {
                    ...this.report,
                    startDate: formGroups?.publicationFrequency?.startDate,
                    publicationFrequency: formGroups?.publicationFrequency?.publicationFrequency,
                };
                break;
            case 'sharing':
                this.report = {
                    ...this.report,
                    users: formGroups?.sharing?.users ?? [],
                };
                break;
            case 'site':
                this.report = {
                    ...this.report,
                    siteAssets: [
                        {
                            id: formGroups?.site?.siteAsset?.id,
                            name: formGroups?.site?.siteAsset?.name,
                            type: formGroups?.site?.siteAsset?.type,
                        },
                    ],
                };

                break;
            default:
                break;
        }
    }

    /**
     *  report details which are used in the edition detail page
     *  @param {ReportDetails} details
     */

    setReportDetails(details: ReportDetails): void {
        this.report = {
            ...this.report,
            guid: details.id,
            name: details.name,
            description: details.description,
            shareList: details.shareList,
            recurrency: details.reportType,
            siteAssets: [
                {
                    id: details.siteId,
                    name: details.location,
                },
            ],
        };
    }

    /**
     * Set definition name and description
     * @param {string} name : name of the definition
     * @param {string} description : description of the definition
     */
    setDefinition(name: string, description: string) {
        this.report = { ...this.report, name, description };
    }

    get template() {
        return this.report.template;
    }

    putRegenerateEdition(reportId: string, reportEditionId: string): Observable<any> {
        const regenerateEdition: RegenerateEditionDto = {
            reportId: reportId,
            resendNotifications: true,
            reshareAttachments: true,
            reportEditionId: reportEditionId,
        };

        const url: string = this.reportsConfiguration.apiPaths.putRegenerateEdition(
            reportId,
            reportEditionId,
        );

        return this.http
            .put<any>(url, regenerateEdition)
            .pipe(
                map(
                    (periodValidationStatus: ReportEditionState): ReportEditionState =>
                        periodValidationStatus,
                ),
            );
    }

    getReport(reportId: string): Observable<ReportDetails> {
        const url: string = this.reportsConfiguration.apiPaths.readUpdateDeleteReport(reportId);

        return this.http.get<GetReportDto>(url).pipe(
            map((dto) => {
                const nextPublicationDate = dto.nextPublicationDate
                    ? new Date(dto.nextPublicationDate)
                    : null;

                return new ReportDetails({
                    name: dto.name,
                    description: dto.description,
                    icon: dto.icon,
                    location: dto.location,
                    nextPublicationDate,
                    reportRights: dto.reportRights,
                    reportType: dto.reportType,
                    shareList: dto.shareList,
                    id: dto.id,
                    siteId: dto.siteId,
                    state: dto.state,
                });
            }),
        );
    }

    getReportEditions(reportId: string): Observable<ReportEditionListItem[]> {
        const url: string = this.reportsConfiguration.apiPaths.getReportEditions(reportId);

        return this.http.get<GetReportEditionsDto[]>(url).pipe(
            map((result) => {
                return result.map((dto) => {
                    const fromDate = new Date(dto.fromDate);
                    const toDate = new Date(dto.toDate);
                    const publicationDate = new Date(dto.publicationDate);

                    return new ReportEditionListItem(dto.reportEditionId, fromDate, toDate, publicationDate);
                });
            }),
        );
    }

    getReportEdition(reportId: string, reportEditionId: string): Observable<ReportEditionDetails> {
        const url: string = this.reportsConfiguration.apiPaths.getReportEdition(reportId, reportEditionId);

        return this.http.get<GetReportEditionDto>(url).pipe(
            map((dto) => {
                const fromDate = new Date(dto.fromDate);
                const toDate = new Date(dto.toDate);
                const publicationDate = new Date(dto.publicationDate);
                const template = this.reportTemplateAdapter.adaptFromDtoToTemplate(dto.template);

                return new ReportEditionDetails({
                    reportEditionId: dto.reportEditionId,
                    fromDate,
                    toDate,
                    publicationDate,
                    usedTimeZoneForSite: dto.usedTimeZoneForSite,
                    template,
                    data: dto.data,
                    state: dto.state,
                    canRegenerate: dto.canRegenerate,
                });
            }),
        );
    }

    /**
     * Save the report definition to the API
     * Sets the specific required properties based on recurring/one-off and share settings
     */

    updateReport(report: ReportDetails | UpdateReportDto): Observable<any> {
        // set generic properties
        const saveObj: UpdateReportDto = {
            name: report.name,
            description: report.description,
            shareList: report.shareList,
            shareType: report.shareList?.length > 0 ? ShareType.Shared : ShareType.Off,
            reportType: report.reportType,
        };

        const url: string = this.reportsConfiguration.apiPaths.readUpdateDeleteReport(report.id);

        return this.http.put<any>(url, saveObj);
    }

    /**
     * Save the report definition to the API
     * Sets the specific required properties based on recurring/one-off and share settings
     */

    saveReportV2(formData: any): Observable<ReportDetails> {
        // set properties
        const reportPeriod =
            formData.recurrency.recurrency === ReportType.OneOff
                ? this.reportPeriodService.getSelectedOneOffOption(formData.frequency.period.selected).period
                : this.reportPeriodService.getSelectedRecurringOption(formData.frequency.period.selected)
                      .period;

        const saveObj: CreateReportDto = {
            defaultTemplateId: this.report.template,
            siteAssets: formData.site.siteAssets,
            name: formData.definition.name,
            description: formData.definition.description,

            reportType: formData.recurrency.recurrency,
            reportPeriod: reportPeriod,

            shareType:
                formData.sharing.users && formData.sharing.users.length > 0
                    ? ShareType.Shared
                    : ShareType.Off,
            shareList: reportDtoHelper.adaptToRecipientInfoDto(formData.sharing.users),
        };

        // set properties for one-off and recurring
        switch (saveObj.reportType) {
            case ReportType.Recurring:
                this.setRecurringReportPropertiesV2(saveObj, formData);
                break;
            case ReportType.OneOff:
                this.setOneOffReportPropertiesV2(saveObj, formData);
                break;
            default:
                break;
        }

        this.trackEventsService.track(TrackEventsHelper.reportCreated(saveObj));

        const url: string = this.reportsConfiguration.apiPaths.createReport();

        return this.http.post<any>(url, saveObj).pipe(
            map((dto) => {
                const nextPublicationDate = dto.nextPublicationDate
                    ? new Date(dto.nextPublicationDate)
                    : null;

                return new ReportDetails({
                    name: dto.name,
                    description: dto.description,
                    icon: dto.icon,
                    location: dto.location,
                    nextPublicationDate,
                    reportRights: dto.reportRights,
                    reportType: dto.reportType,
                    shareList: dto.shareList ? dto.shareList : [],
                    id: dto.id,
                    siteId: dto.siteId,
                    state: dto.state,
                });
            }),
        );
    }

    saveReportV3(reportDto: CreateReportDto): Observable<ReportDetails> {
        this.trackEventsService.track(TrackEventsHelper.reportCreated(reportDto));

        const url: string = this.reportsConfiguration.apiPaths.createReport();
        return this.http.post<ReportDetails>(url, reportDto).pipe(
            map((dto) => {
                const nextPublicationDate = dto.nextPublicationDate
                    ? new Date(dto.nextPublicationDate)
                    : null;

                return new ReportDetails({
                    name: dto.name,
                    description: dto.description,
                    icon: dto.icon,
                    location: dto.location,
                    nextPublicationDate,
                    reportRights: dto.reportRights,
                    reportType: dto.reportType,
                    shareList: dto.shareList ? dto.shareList : [],
                    id: dto.id,
                    siteId: dto.siteId,
                    state: dto.state,
                });
            }),
        );
    }

    duplicateReport(reportDto: DuplicateReportDto): Observable<ReportListItemDto> {
        this.trackEventsService.track({
            type: AnalyticsTrackEvent.newReportCreated,
        });

        const url: string = this.reportsConfiguration.apiPaths.duplicateReport();
        return this.http
            .post<ReportListItemDto>(url, reportDto)
            .pipe(map((reportListItem: ReportListItemDto) => reportListItem));
    }

    /**
     * Activate/Publish the report
     * This will schedule the report for edition generation on the scheduling service
     */

    activateReport(): Observable<any> {
        this.trackEventsService.track(TrackEventsHelper.reportActivated(this.report));

        const url: string = this.reportsConfiguration.apiPaths.activateReport(this.report.guid);

        return this.http.post<any>(url, {});
    }

    getReportShareList(reportId: string): Observable<[RecipientInfoDto]> {
        const url: string = this.reportsConfiguration.apiPaths.getReportShareList(reportId);

        return this.http.get<[RecipientInfoDto]>(url);
    }

    deleteReport(reportId: string): Observable<any> {
        const url: string = this.reportsConfiguration.apiPaths.readUpdateDeleteReport(reportId);

        return this.http.delete<any>(url, {});
    }

    private setOneOffReportPropertiesV2(saveObj: CreateReportDto, formData: any) {
        const dates = formData['frequency'].period.dates;
        const selectedOneOffOption = this.reportPeriodService.getSelectedOneOffOption(
            formData.frequency.period.selected,
        );

        switch (formData.frequency.period.selected) {
            case Options.CUSTOM:
                saveObj.fromDateOneOff = dates.startDate.format(REPORT_DATE_FORMAT);
                saveObj.toDateOneoff = dates.endDate.format(REPORT_DATE_FORMAT);
                break;
            default:
                saveObj.fromDateOneOff = selectedOneOffOption.fromDate;
                saveObj.toDateOneoff = selectedOneOffOption.toDate;
                break;
        }
    }

    private setRecurringReportPropertiesV2(saveObj: CreateReportDto, formData: any) {
        saveObj.publishingStartDate =
            formData['frequency'].publicationFrequency.startDate.format(REPORT_DATE_FORMAT);
        saveObj.publishingFrequency = formData['frequency'].publicationFrequency.publicationFrequency;
    }
}
